import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { getPaletteColor } from '@/core/lib/colors';
import { StatusPopoverForm } from './StatusPopoverForm';
import type { TemplateStatus } from '../types';
import type { StatusPopoverFormValues } from '../schemas';

interface TemplateStatusesTabProps {
  statuses: TemplateStatus[];
  editable: boolean;
  onAdd?: (values: StatusPopoverFormValues) => void;
  onRemove?: (index: number) => void;
  isPending?: boolean;
}

export function TemplateStatusesTab({
  statuses,
  editable,
  onAdd,
  onRemove,
  isPending = false,
}: TemplateStatusesTabProps) {
  const { t } = useTranslation('projectTemplates');

  return (
    <div className="flex flex-col gap-2" data-testid="project-templates-statuses-tab">
      {statuses.length === 0 ? (
        <p
          className="py-4 text-center text-sm text-muted-foreground"
          data-testid="project-templates-statuses-empty"
        >
          {t('preview.emptyStates.statuses')}
        </p>
      ) : (
        statuses.map((status, index) => {
          const colorValue = getPaletteColor(status.color)?.value;
          return (
            <div
              key={`${status.name}-${index}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              data-testid={`project-templates-status-row-${index}`}
            >
              <div className="flex items-center gap-2">
                <div
                  className="size-3 rounded-full border border-border"
                  style={colorValue ? { background: colorValue } : undefined}
                />
                <span className="text-sm">{status.name}</span>
              </div>
              {editable && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      disabled={isPending}
                      aria-label={t('actions.remove')}
                      onClick={() => onRemove?.(index)}
                      data-testid={`project-templates-status-remove-${index}`}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.remove')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })
      )}

      {editable && onAdd && <StatusPopoverForm onAdd={onAdd} isPending={isPending} />}
    </div>
  );
}
